import {
  KEY_ADMIN_TOKEN,
  KEY_USER,
  KEY_USER_TOKEN,
  KEY_NORMAL_USER,
} from "../utils/preferences";

/**
 * Which side of the application someone is on, and what belongs to that side.
 *
 * Two keys per side, because both matter: one carries the token, the other is
 * what the routes look at to decide whether someone is logged in. Leaving the
 * second behind sends people to a screen that still thinks they are.
 *
 * The admin panel is reached two ways — a path under /admin/ and its own host
 * suitesadmin — and only the first one shows in the path. Deciding on the path
 * alone put anyone on that host on the booker side.
 */
const SIDES = [
  {
    name: "admin",
    matches: (pathname, host) =>
      pathname.startsWith("/admin/") || host.startsWith("suitesadmin"),
    loginScreen: "/admin/login",
    tokenKey: KEY_ADMIN_TOKEN,
    userKey: KEY_USER,
  },
  {
    name: "booker",
    matches: () => true,
    loginScreen: "/",
    tokenKey: KEY_USER_TOKEN,
    userKey: KEY_NORMAL_USER,
  },
];

/** The side the current page belongs to. Never undefined: booker matches all. */
export function currentSide(win = window) {
  const pathname = win.location?.pathname ?? "";
  const host = String(win.location?.host ?? "");
  return SIDES.find((side) => side.matches(pathname, host));
}

/**
 * The token to send along, belonging to the side being used.
 *
 * There used to be a fallback here: if the side had no token, any other token
 * in storage was used. That existed because the side was decided on the path
 * alone, so everyone on the suitesadmin host ended up looking for a booker
 * token and needed the fallback to find the admin one. Now that the host is
 * taken into account the fallback is not needed — and it was harmful: after
 * clearing one side's keys the next call would quietly pick up the other
 * side's token, so someone who had just been logged out was not.
 */
export function getAuthToken(win = window, store = window.localStorage) {
  const side = currentSide(win);
  try {
    const stored = store.getItem(side.tokenKey);
    return stored ? JSON.parse(stored)?.token : undefined;
  } catch (error) {
    // Unreadable or malformed storage means: no token.
    return undefined;
  }
}

function hasToken(side, store) {
  try {
    return Boolean(store.getItem(side.tokenKey));
  } catch (error) {
    return false;
  }
}

/**
 * Handles a 401 from the backend: clear the token and go to the login screen.
 *
 * A 401 means the token no longer deserves trust, and nothing else in the
 * application answers with that code. Without this the screen kept its empty
 * template — the answer used to be a 500 that no one could tell apart from a
 * server failure, and the only way out was logging out and back in by hand.
 *
 * Only the keys of the side being used are cleared. Someone working in the
 * admin panel and the booker app at the same time would otherwise log
 * themselves out of the other one.
 *
 * The two arguments exist so a test can hand in its own window and storage.
 * Callers pass nothing.
 */
export function handleUnauthorized(win = window, store = window.localStorage) {
  const side = currentSide(win);
  const pathname = win.location?.pathname ?? "";
  // On the login screen without a token there is nothing to clear, and
  // reloading would loop. With a token it is not the login screen at all: the
  // booker app's start screen shares its address, so a 401 there (an expired
  // token, or the maintenance switch) must still clear and reload.
  if (pathname === side.loginScreen && !hasToken(side, store)) return false;

  try {
    store.removeItem(side.tokenKey);
    store.removeItem(side.userKey);
  } catch (error) {
    // A browser that refuses storage still needs to reach the login screen.
  }
  win.location.href = side.loginScreen;
  return true;
}
